import { type ReactNode } from 'react';

import { HStack } from '@/components/ui/hstack';
import { MINIMUM_SPACING_BETWEEN_TARGETS } from '@/util/accessibleTouchTarget';

/**
 * Row of inquiry-level actions shown directly above the composer.
 *
 * Inquiry actions (On your honor today; Leave feedback next) act on the whole
 * inquiry, not the message being typed, so they live here rather than in the
 * message toolbar, whose tools (attach, reference, send) act on the outgoing
 * message. Mixing the two scopes in one toolbar mislabels an inquiry action as a
 * message tool.
 *
 * A borderless, right-aligned cluster of action buttons — no card frame and no
 * label. The buttons carry their own affordance (design-system `Button`), so the
 * row doesn't need a container or an eyebrow to read as actions; dropping them
 * keeps the area above the composer light. Right-aligned to mirror the composer's
 * send; a second action joins as a right cluster.
 *
 * Context-dependent by construction: the caller renders this only when at least
 * one inquiry action applies (an eligible expert on an active inquiry), so the
 * row collapses to nothing the rest of the time rather than sitting empty. Kept
 * presentational on purpose; gating and the action set live with the caller. Do
 * not add per-action logic here.
 *
 * The row is the last item in the floating composer overlay, so it sits nearest the
 * composer with the nudge banners stacked above it. It carries no fixed height: the
 * row once reserved one to stop the message column reflowing while the toggle swapped
 * to an inline confirm, and that confirm now lives in the reason sheet, so the slack
 * only padded the overlay. Spacing is margins, which is what actually controls the
 * gaps.
 *
 * Vertical space reads as equal above and below: `mb-2` sets 8px down to the
 * composer, and the banner's own matching `mb-2` supplies the 8px above. Resist
 * making these asymmetric to "group" the row with the composer — a lone pill in the
 * gutter between two bordered cards has no visual set to belong to, so unequal gaps
 * read as a centering mistake rather than as grouping.
 *
 * The action gap is `MINIMUM_SPACING_BETWEEN_TARGETS`, the same 12px the message
 * toolbar uses, so adjacent targets stay far enough apart to hit cleanly once a
 * second action joins.
 */
export function InquiryActionBar({ children }: { children: ReactNode }) {
  // `self-end` shrinks the row to its content so it only occupies the space its
  // buttons actually cover. The row floats over the chat, so a full-width box would
  // keep a transparent hitbox across the whole width and swallow presses on whatever
  // sits behind its empty side. Sizing to content keeps the right-aligned look while
  // leaving the empty left side click-through.
  return (
    <HStack
      className="mb-2 items-center justify-end self-end"
      style={{ gap: MINIMUM_SPACING_BETWEEN_TARGETS }}>
      {children}
    </HStack>
  );
}
